import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useLocation } from '@reach/router';

import CloseIcon from '../../../svg/exit.svg';

import { ArrowButton } from '../../ui/links';
import { PersistantModal } from '../../modals/persistantModal';
import RangePicker from '../../ui/dateRangePicker';

const inputClasses = `bg-marphonaInput px-4 py-2 w-full placeholder-current appearance-none`;

const inputClassesAlt = `bg-marphonaBrown px-4 py-2 w-full placeholder-current border border-marphonaGold appearance-none`;

function encode(data) {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&');
}

const BookingForm = ({ onClose }) => {
  const location = useLocation();
  return (
    <div className="w-full h-full bg-marphonaBrown text-white px-gutter overflow-scroll lg:flex lg:items-start lg:justify-center lg:pb-24">
      <div className="max-w-2xl w-full pb-32 lg:pb-0">
        <header className="flex items-center justify-between w-full">
          <p className="text-marphonaGold text-3xl font-display">Booking form</p>
          <button onClick={onClose} className="w-6">
            <CloseIcon className="text-sand fill-current stroke-current" />
          </button>
        </header>
        {/* Form */}
        <Formik
          initialValues={{
            name: '',
            email: '',
            organisation: '',
            address: '',
            phone: '',
            guests: '1-5',
            checkin: '',
            checkOut: '',
          }}
          validate={(values) => {
            const errors = {};
            if (!values.email) {
              errors.email = 'Required';
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
              errors.email = 'Invalid email address';
            }
            if (!values.phone) {
              errors.phone = 'Required';
            }
            if (!values.guests) {
              errors.guests = 'Required';
            }
            return errors;
          }}
          onSubmit={(values, { setSubmitting, setStatus }) => {
            fetch(location.pathname, {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: encode({
                'form-name': `experience-form`,
                ...values,
              }),
            })
              .then(() => {
                setSubmitting(false);
                setStatus('success');
              })
              .catch((error) => alert(error));
          }}
        >
            {({ onChange, isSubmitting, status, setFieldValue }) => (
            <Form
              name="experience-form"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              subject="Experience Enquiry"
            >
              {/* Hidden Fields for netlify */}
              <input type="hidden" name="form-name" value="experience-form" />
              <p hidden>
                <label>
                  <input name="bot-field" onChange={onChange} />
                </label>
              </p>
              {status === 'success' ? (
                <div className="text-left">
                  <motion.p
                    className="font-display text-2xl"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                  >
                    Thank you we will be in touch.
                  </motion.p>
                </div>
              ) : (
                <div className="grid gap-y-6">
                  <div>
                    <Field type="text" name="name" className={inputClasses} placeholder="Name" />
                  </div>
                  <div>
                    <Field
                      type="text"
                      name="organisation"
                      className={inputClasses}
                      placeholder="Organisation"
                    />
                  </div>
                  <div>
                    <Field
                      type="email"
                      name="email"
                      className={inputClasses}
                      placeholder="Email Address"
                      required
                    />
                    <ErrorMessage name="email" component="div" className="text-sm mt-3" />
                  </div>
                  <div>
                    <Field
                      type="text"
                      name="address"
                      className={inputClasses}
                      placeholder="Address"
                    />
                  </div>
                  <div>
                    <Field
                      type="text"
                      name="phone"
                      className={inputClasses}
                      placeholder="Phone"
                      required={true}
                    />
                    <ErrorMessage name="phone" component="div" className="text-sm mt-3" />
                  </div>
                  <div>
                    <label>Group Size</label>
                    <Field
                      type="text"
                      name="guests"
                      className={inputClasses}
                      placeholder="Guests"
                      required={true}
                      as="select"
                    >
                      <option value="1-5">1-5</option>
                      <option value="5-10">5-10</option>
                      <option value="10-20">10-20</option>
                      <option value="20-30">20-30</option>
                      <option value="30-40">30-40</option>
                      <option value="40+">40+</option>
                    </Field>
                    <ErrorMessage name="guests" component="div" className="text-sm mt-3" />
                  </div>
                  <div>
                    <RangePicker
                      onChange={(checkin, checkOut) => {
                        setFieldValue('checkin', checkin);
                        setFieldValue('checkOut', checkOut);
                      }}
                    />
                    <Field type="hidden" name="checkin" />
                    <Field type="hidden" name="checkOut" />
                  </div>

                  <div className="border-t-2 border-dashed border-marphonaGold pt-6 mt-2">
                    <ArrowButton arrowColor="text-marphonaGold" className="text-2xl">
                      Enquire Now
                    </ArrowButton>
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export const ExperienceForm = ({ linkText }) => {
  const [isOpen, setOpen] = useState(false);
  return (
    <>
      <ArrowButton arrowColor="text-marphonaGold" onClick={() => setOpen(true)}>
        {linkText}
      </ArrowButton>
      <PersistantModal isOpen={isOpen}>
        <BookingForm onClose={() => setOpen(false)} />
      </PersistantModal>
    </>
  );
};
